/**
 * Pricing definitions for the public marketing pages (landing + pricing).
 *
 * Coval Flow is sold through two payment modes for the same product:
 *
 * - `event`: a one-off payment for a single event, priced by volunteer count.
 *   This fits the seasonal half of the market (one festival a year) where a
 *   monthly subscription would be cancelled right after the event.
 * - `subscription`: a recurring plan for organisers running several events a
 *   year.
 *
 * These values are NOT the source of truth: the `plans` and `event_tiers`
 * tables are, and the backend enforces them. They are the bundled fallback,
 * used until `useCatalogue` has answered and whenever it cannot — a pricing
 * page showing no price at all is worse than one a deploy behind. Keep them in
 * step with the seed of migration 051.
 *
 * All prices are VAT included (TTC).
 */

/** Number of months in a year, used to annualize a monthly price. */
const MONTHS_PER_YEAR = 12;

/**
 * Usage dimensions the backend caps. Mirrors `QUOTA_DIMENSION` in
 * `backend/src/helpers/constants.ts` — a quota breach comes back as a 402
 * carrying one of these values.
 */
export const QUOTA_DIMENSION = {
  ACTIVE_EVENTS: "active_events",
  VOLUNTEERS_PER_EVENT: "volunteers_per_event",
  TEAM_MEMBERS: "team_members",
  CONTRACT_SIGNATURES: "contract_signatures",
} as const;

export type QuotaDimension =
  (typeof QUOTA_DIMENSION)[keyof typeof QUOTA_DIMENSION];

/**
 * Features a plan either carries or not. Mirrors `PLAN_CAPABILITY` in
 * `backend/src/helpers/constants.ts`.
 */
export const PLAN_CAPABILITY = {
  EXPORT_PLANNING: "export_planning",
  CUSTOMIZE_CONTRACT_TEMPLATES: "customize_contract_templates",
  IMPORT_VOLUNTEERS: "import_volunteers",
  AUTO_REMIND_SIGNATURES: "auto_remind_signatures",
  RESTRICT_EVENT_ACCESS: "restrict_event_access",
} as const;

export type PlanCapability =
  (typeof PLAN_CAPABILITY)[keyof typeof PLAN_CAPABILITY];

/** A refused action because a counter is full. */
export interface QuotaDetails {
  dimension: QuotaDimension;
  current: number;
  limit: number;
  /**
   * Present when the breach belongs to one event. It is what lets the prompt
   * offer to unlock THAT event straight away instead of only linking to the
   * pricing page.
   */
  event_id?: number;
  /**
   * How much room the action needs, as stated by the guard that refused it.
   *
   * Sizing an offer from `current` alone cannot work: one guard is projected
   * (`current + 1 > limit`) and the other is not (`current > limit`), so the
   * client had to guess which one had spoken. A blanket `+1` sold the next
   * package UP at every boundary — 99 € for a 50-person event the 49 € package
   * covers exactly. Optional, because an older backend does not send it.
   */
  required?: number;
}

/** A refused action because the plan does not carry the feature at all. */
export interface CapabilityDetails {
  capability: PlanCapability;
}

/**
 * What the backend attaches to a 402. Either shape can arrive: a counter that
 * filled up, or a feature that is not part of the plan.
 */
export type UpgradeReason = QuotaDetails | CapabilityDetails;

export const isCapabilityReason = (
  reason: UpgradeReason,
): reason is CapabilityDetails => "capability" in reason;

export const PRICING_MODES = ["event", "subscription"] as const;

export type PricingMode = (typeof PRICING_MODES)[number];

/* -------------------------------------------------------------------------- */
/* Subscription                                                               */
/* -------------------------------------------------------------------------- */

export const PLAN_IDS = ["free", "pro", "business"] as const;

export type PlanId = (typeof PLAN_IDS)[number];

/** Entry plan every workspace starts on: no payment, no feature intro line. */
export const FREE_PLAN_ID: PlanId = "free";

export type BillingPeriod = "monthly" | "yearly";

export interface PlanPricing {
  id: PlanId;
  /** Price in EUR per month when billed monthly. */
  monthly: number;
  /** Price in EUR per month when billed yearly. */
  yearly: number;
  /** Visually emphasized as the recommended plan. */
  highlighted: boolean;
}

export const PLAN_PRICING: Record<PlanId, PlanPricing> = {
  free: { id: "free", monthly: 0, yearly: 0, highlighted: false },
  pro: { id: "pro", monthly: 29, yearly: 24, highlighted: true },
  business: { id: "business", monthly: 79, yearly: 65, highlighted: false },
};

/**
 * Rounded saving a plan gets from yearly billing. Takes the pricing as an
 * argument so it can be computed from live catalogue values, not only from the
 * bundled ones.
 */
export const getYearlySavingPercent = (pricing: PlanPricing): number =>
  pricing.monthly === 0
    ? 0
    : Math.round((1 - pricing.yearly / pricing.monthly) * 100);

/** Saving on the bundled Pro price, used when the catalogue is unavailable. */
export const YEARLY_SAVING_PERCENT = getYearlySavingPercent(PLAN_PRICING.pro);

/**
 * Effective caps of a plan. `null` means unlimited.
 *
 * These mirror the `plans` table seeded by migration 051 and act as the
 * fallback when the catalogue API cannot be reached — the database stays the
 * source of truth, and no figure is ever written twice by hand.
 */
export interface PlanLimits {
  activeEvents: number | null;
  volunteersPerEvent: number | null;
  teamMembers: number | null;
  contractSignatures: number | null;
}

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  free: {
    activeEvents: 1,
    volunteersPerEvent: 10,
    teamMembers: 2,
    contractSignatures: 5,
  },
  pro: {
    activeEvents: null,
    volunteersPerEvent: 150,
    teamMembers: 5,
    contractSignatures: null,
  },
  business: {
    activeEvents: null,
    volunteersPerEvent: null,
    teamMembers: null,
    contractSignatures: null,
  },
};

/**
 * Quota rows of the comparison table, in display order. Their figures come from
 * {@link PlanLimits}, so the table can never drift from what is enforced; only
 * their labels live in i18n.
 */
export const COMPARISON_QUOTA_ROWS = [
  "activeEvents",
  "volunteersPerEvent",
  "teamMembers",
  "contractSignatures",
] as const;

export type ComparisonQuotaRow = (typeof COMPARISON_QUOTA_ROWS)[number];

/**
 * One row of the qualitative part of the comparison table — features a plan
 * either has or hasn't. Fully resolved from i18n (`pricing.compare.rows`);
 * a boolean renders as a check / dash icon.
 */
export type PlanComparisonRow = { label: string } & Record<
  PlanId,
  boolean | string
>;

/* -------------------------------------------------------------------------- */
/* Per-event                                                                  */
/* -------------------------------------------------------------------------- */

export const EVENT_TIER_IDS = ["discovery", "s", "m", "l", "xl"] as const;

export type EventTierId = (typeof EVENT_TIER_IDS)[number];

/** The only event tier that costs nothing. */
export const FREE_EVENT_TIER_ID: EventTierId = "discovery";

export interface EventTierPricing {
  id: EventTierId;
  /** Maximum number of volunteers covered by the tier. */
  volunteers: number;
  /** One-off price in EUR, VAT included. */
  price: number;
  /** Visually emphasized as the most common tier. */
  highlighted: boolean;
}

export const EVENT_TIER_PRICING: Record<EventTierId, EventTierPricing> = {
  discovery: { id: "discovery", volunteers: 10, price: 0, highlighted: false },
  s: { id: "s", volunteers: 50, price: 49, highlighted: false },
  m: { id: "m", volunteers: 150, price: 99, highlighted: true },
  l: { id: "l", volunteers: 400, price: 199, highlighted: false },
  xl: { id: "xl", volunteers: 1000, price: 399, highlighted: false },
};

/** Cheapest paid event tier, advertised as the per-event entry price. */
export const EVENT_ENTRY_PRICE = EVENT_TIER_PRICING.s.price;

/**
 * The smallest paid package that would cover `volunteers`, or null when even
 * the largest one falls short — that conversation happens by email.
 */
export const findEventTierFor = (volunteers: number): EventTierPricing | null =>
  EVENT_TIER_IDS.map((id) => EVENT_TIER_PRICING[id])
    .filter((tier) => tier.price > 0)
    .find((tier) => tier.volunteers >= volunteers) ?? null;

/**
 * Months of full access around the event date. Past that window the event
 * turns read-only — exports stay available.
 */
export const EVENT_ACCESS_MONTHS = 6;

/**
 * Number of events per year above which the yearly Pro subscription costs less
 * than paying for each mid-size event separately. Advertised as-is so the
 * arbitration between both modes stays verifiable by the customer.
 */
export const SUBSCRIPTION_BREAK_EVEN_EVENTS = Math.ceil(
  (PLAN_PRICING.pro.yearly * MONTHS_PER_YEAR) / EVENT_TIER_PRICING.m.price,
);

/* -------------------------------------------------------------------------- */
/* Credit packs                                                               */
/* -------------------------------------------------------------------------- */

/** Event tiers sold as prepaid credit packs. */
export const CREDIT_PACK_TIER_IDS = ["s", "m", "l"] as const;

export type CreditPackTierId = (typeof CREDIT_PACK_TIER_IDS)[number];

/** Number of event credits in a pack. */
export const CREDIT_PACK_SIZE = 3;

/** Discount applied to a pack compared to buying each event separately. */
export const CREDIT_PACK_DISCOUNT_PERCENT = 20;

/** How long a purchased credit stays usable. */
export const CREDIT_PACK_VALIDITY_MONTHS = 24;

/**
 * Pack prices are derived from the tier price the caller passes in, not read
 * from the bundled constants: the page shows live catalogue prices, and a pack
 * computed on a stale figure would contradict the tier card right next to it.
 */

/** Full price of a pack bought event by event, before the pack discount. */
export const getCreditPackFullPrice = (tierPrice: number): number =>
  tierPrice * CREDIT_PACK_SIZE;

/** Discounted price of a pack of {@link CREDIT_PACK_SIZE} event credits. */
export const getCreditPackPrice = (tierPrice: number): number =>
  Math.floor(
    getCreditPackFullPrice(tierPrice) *
      (1 - CREDIT_PACK_DISCOUNT_PERCENT / 100),
  );

/** Effective price of a single event once bought inside a pack. */
export const getCreditPackUnitPrice = (tierPrice: number): number =>
  Math.round(getCreditPackPrice(tierPrice) / CREDIT_PACK_SIZE);
